/***
 *
 *   SIGN UP STEP 2
 *   Verify email address
 *   Step 1: create account
 *   Step 2: verify email address
 *   Step 3: select plan
 *
 **********/

import { useContext, useState, useEffect, useRef } from 'react';
import { AuthContext, useLocation, useNavigate } from 'components/lib';
import { SIGN_UP_EMAIL_KEY } from '../../../app/auth';
import { sendVerificationEmail, verifyEmail } from '../../../lib/auth-client';
import { UMButton } from '@urbanmetrix/um-kit';
import { InfoIcon, LogoutIcon } from 'components/icons';

type SignupVerificationProps = Record<string, never>;

interface VerificationMessageButton {
  text: string;
  action: () => Promise<void>;
}

interface VerificationMessage {
  type: string;
  title: string;
  text: string;
  confirmation: string;
  button?: VerificationMessageButton | false;
}

interface VerifyErrorResponse {
  response?: { status?: number };
}

export function SignupVerification(_props: SignupVerificationProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const qs = location.search;
  const authContext = useRef(useContext(AuthContext));
  const emailFromQuery = new URLSearchParams(qs).get('email');

  const [message, setMessage] = useState<VerificationMessage>({
    type: 'info',
    title: 'Please Check Your Email',
    text: 'Please click the link in the email we sent to verify your account.',
    confirmation: '',
    button: {
      text: 'Re-Send Verification Email',
      action: resendVerificationEmail,
    },
  });

  useEffect(() => {
    if (!qs && authContext.current.isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, qs]);

  useEffect(() => {
    const verifyToken = async (token: string) => {
      console.log('token verify ', token);
      try {
        setMessage({
          type: 'success',
          title: 'Verifying...',
          text: 'Please wait a moment while we verify your email.',
          confirmation: '',
          button: false,
        });

        await verifyEmail(token);
        await authContext.current.refreshSession?.();
        authContext.current.update({ verified: true });

        return navigate('/dashboard');
      } catch (err) {
        // // token isnt for this account, force signout
        if ((err as VerifyErrorResponse)?.response?.status === 401) return authContext.current.signout();

        setMessage({
          type: 'error',
          title: 'Verification Link Has Expired',
          text: 'Please re-send the verification email and try again',
          confirmation: '',
          button: {
            text: 'Re-Send Verification Email',
            action: resendVerificationEmail,
          },
        });
      }
    };

    if (qs.includes('?token=')) {
      // check token exists
      verifyToken(qs.substring(qs.indexOf('?token=') + 7));
    }
  }, [qs, authContext, navigate]);

  async function resendVerificationEmail() {
    setMessage({
      type: 'success',
      title: 'Please Check Your Email',
      text: 'Please click the link in the email we sent to verify your account.',
      confirmation: '',
    });

    const email =
      authContext.current.user?.email ||
      location.state?.signUpEmail ||
      emailFromQuery ||
      sessionStorage.getItem(SIGN_UP_EMAIL_KEY);

    if (!email) {
      setMessage({
        type: 'error',
        title: 'Unable To Re-Send Verification Email',
        text: 'We could not find your email address. Please go back to login and try again.',
        confirmation: '',
        button: false,
      });
      return;
    }

    sessionStorage.setItem(SIGN_UP_EMAIL_KEY, email);
    await sendVerificationEmail(email, '/email-confirmed');
    setMessage({
      type: 'success',
      title: 'Please Check Your Email',
      text: 'Please click the link in the email we sent to verify your account.',
      confirmation: 'Verification email sent.',
      button: false,
    });
  }

  async function handleLogout() {
    try {
      sessionStorage.removeItem(SIGN_UP_EMAIL_KEY);
    } catch (error) {}

    if (authContext.current?.isAuthenticated) {
      await authContext.current.signout?.();
      return;
    }

    window.location.href = '/login';
  }

  return (
    <div className="plan-readonly-shell auth-verify-shell">
      <div className="plan-readonly-panel auth-verify-panel">
        <div className="plan-readonly-panel__topbar">
          <button
            className="signup-plan-page-logout"
            type="button"
            onClick={handleLogout}
            title="Logout"
            aria-label="Logout"
          >
            <LogoutIcon style={{ width: 18, height: 18 }} />
            <span>Logout</span>
          </button>
        </div>

        <div className="auth-verify-card__content">
          {message.title && (
            <div className="auth-verify-card__headline">
              <div className="auth-verify-card__icon" aria-hidden="true">
                <InfoIcon />
              </div>
              <h1 className="auth-verify-card__title">{message.title}</h1>
            </div>
          )}
          {message.text && <p className="auth-verify-card__text">{message.text}</p>}
          {message.confirmation && <div className="auth-verify-card__confirmation">{message.confirmation}</div>}
          {message.button && message.button.action && (
            <div className="auth-verify-card__button">
              <UMButton
                className="auth-verify-card__button-control"
                text={message.button.text}
                onClick={message.button.action}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
